import { useState, useEffect } from 'react'
import { Trash2, Eye, Download, Share2, Calendar, BarChart3 } from 'lucide-react'
import { SprintReportRecord } from '../lib/sprintReportService'
import { sprintReportService } from '../lib/sprintReportService'
import { exportService } from '../lib/exportService'

interface SprintReportHistoryProps {
  onViewReport: (report: SprintReportRecord) => void
}

export default function SprintReportHistory({ onViewReport }: SprintReportHistoryProps) {
  const [reports, setReports] = useState<SprintReportRecord[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [deleting, setDeleting] = useState<string | null>(null)

  useEffect(() => {
    loadReports()
  }, [])

  const loadReports = async () => {
    try {
      setLoading(true)
      setError(null)
      const reportsData = await sprintReportService.getReports()
      setReports(reportsData)
    } catch (error) {
      console.error('Failed to load reports:', error)
      setError('Failed to load sprint reports')
    } finally {
      setLoading(false)
    }
  }

  const handleDeleteReport = async (reportId: string) => {
    if (!confirm('Are you sure you want to delete this report? This action cannot be undone.')) {
      return
    }

    try {
      setDeleting(reportId)
      await sprintReportService.deleteReport(reportId)
      setReports(reports.filter(r => r.id !== reportId))
    } catch (error) {
      console.error('Failed to delete report:', error)
      alert('Failed to delete report. Please try again.')
    } finally {
      setDeleting(null)
    }
  }

  const handleExportReport = async (report: SprintReportRecord, type: 'pdf' | 'excel' | 'powerpoint') => {
    try {
      switch (type) {
        case 'pdf':
          await exportService.exportToPDF(report.report_data, report.sprint_details)
          break
        case 'excel':
          await exportService.exportToExcel(report.report_data, report.sprint_details)
          break
        case 'powerpoint':
          await exportService.exportToPowerPoint(report.report_data, report.sprint_details)
          break
      }
    } catch (error) {
      console.error('Export failed:', error)
      alert(`Failed to export ${type.toUpperCase()}. Please try again.`)
    }
  }

  const handleShareReport = async (report: SprintReportRecord) => {
    try {
      const shareableLink = await sprintReportService.generateShareableLink(report.id)
      navigator.clipboard.writeText(shareableLink).then(() => {
        alert('Shareable link copied to clipboard!')
      }).catch(() => {
        alert('Failed to copy link. Please try again.')
      })
    } catch (error) {
      console.error('Share failed:', error)
      alert('Failed to generate shareable link. Please try again.')
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="text-center">
          <h2 className="text-xl font-semibold mb-2">Loading Reports</h2>
          <p className="text-gray-600">Fetching your sprint report history...</p>
        </div>
        <div className="flex justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="text-center">
          <h2 className="text-xl font-semibold mb-2">Error Loading Reports</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={loadReports}
            className="px-4 py-2 bg-primary-500 text-white rounded-md hover:bg-primary-600"
          >
            Try Again
          </button>
        </div>
      </div>
    )
  }

  if (reports.length === 0) {
    return (
      <div className="space-y-6">
        <div className="text-center">
          <BarChart3 className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <h2 className="text-xl font-semibold mb-2">No Reports Yet</h2>
          <p className="text-gray-600">
            You haven't generated any sprint reports yet. Start by analyzing a sprint to see your reports here.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold">Sprint Report History</h2>
          <p className="text-gray-600">View and manage your previous sprint reports</p>
        </div>
        <button
          onClick={loadReports}
          className="px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
        >
          Refresh
        </button>
      </div>

      <div className="space-y-4">
        {reports.map((report) => (
          <div
            key={report.id}
            className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow"
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center space-x-2 mb-2">
                  <BarChart3 className="w-5 h-5 text-blue-600" />
                  <h3 className="text-lg font-medium text-gray-900">
                    {report.sprint_label}
                  </h3>
                </div>
                
                <div className="text-sm text-gray-600 mb-3">
                  <div className="flex items-center space-x-4">
                    <span className="flex items-center space-x-1">
                      <Calendar className="w-4 h-4" />
                      <span>{formatDate(report.created_at)}</span>
                    </span>
                    <span>Board: {report.board_id}</span>
                    <span>Cards: {report.report_data.summary.totalCards}</span>
                    <span>Velocity: {report.report_data.summary.velocity} cards/day</span>
                  </div>
                </div>

                <div className="text-sm text-gray-700">
                  <p><strong>Goal:</strong> {report.sprint_details.goal}</p>
                  <p><strong>Duration:</strong> {report.sprint_details.duration}</p>
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <button
                  onClick={() => onViewReport(report)}
                  className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-md"
                  title="View Report"
                >
                  <Eye className="w-4 h-4" />
                </button>
                
                <div className="relative">
                  <button
                    className="p-2 text-gray-600 hover:text-green-600 hover:bg-green-50 rounded-md"
                    title="Export"
                  >
                    <Download className="w-4 h-4" />
                  </button>
                  <div className="absolute right-0 mt-2 w-32 bg-white border border-gray-200 rounded-md shadow-lg z-10">
                    <button
                      onClick={() => handleExportReport(report, 'pdf')}
                      className="flex items-center space-x-2 w-full px-3 py-2 text-sm hover:bg-gray-50"
                    >
                      <span>PDF</span>
                    </button>
                    <button
                      onClick={() => handleExportReport(report, 'excel')}
                      className="flex items-center space-x-2 w-full px-3 py-2 text-sm hover:bg-gray-50"
                    >
                      <span>Excel</span>
                    </button>
                    <button
                      onClick={() => handleExportReport(report, 'powerpoint')}
                      className="flex items-center space-x-2 w-full px-3 py-2 text-sm hover:bg-gray-50"
                    >
                      <span>PowerPoint</span>
                    </button>
                  </div>
                </div>
                
                <button
                  onClick={() => handleShareReport(report)}
                  className="p-2 text-gray-600 hover:text-purple-600 hover:bg-purple-50 rounded-md"
                  title="Share"
                >
                  <Share2 className="w-4 h-4" />
                </button>
                
                <button
                  onClick={() => handleDeleteReport(report.id)}
                  disabled={deleting === report.id}
                  className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-md disabled:opacity-50"
                  title="Delete Report"
                >
                  {deleting === report.id ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600"></div>
                  ) : (
                    <Trash2 className="w-4 h-4" />
                  )}
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
} 